import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  fetchIntegrations,
  fetchIntegrationStatus,
  type Check,
  type CheckStatus,
  type StudyRow,
} from "../services/integrationStatus";

const DOT: Record<CheckStatus, string> = {
  ok: "bg-green-500",
  warn: "bg-yellow-500",
  bad: "bg-red-500",
  error: "bg-red-600",
  info: "bg-blue-400",
  na: "bg-gray-500",
};

const LABEL: Record<CheckStatus, string> = {
  ok: "OK",
  warn: "Warn",
  bad: "Down",
  error: "Error",
  info: "Info",
  na: "—",
};

function Pill({ c }: { c: Check }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={c.detail}>
      <span className={`inline-block w-2 h-2 rounded-full ${DOT[c.status] ?? DOT.na}`} />
      <span className="text-fg">{c.detail || LABEL[c.status]}</span>
    </span>
  );
}

function HealthCard({ name, c }: { name: string; c: Check }) {
  return (
    <div className="rounded-sharp border border-border bg-surface-1 p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT[c.status] ?? DOT.na}`} />
        <span className="font-medium">{name}</span>
      </div>
      <div className="text-fg-muted text-sm">{c.detail}</div>
    </div>
  );
}

function fmtTime(ts: string | null): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function uidTail(uid: string): string {
  return uid.length > 22 ? "…" + uid.slice(-22) : uid;
}

function fmtDur(s: number | null | undefined): string {
  if (s == null) return "";
  if (s < 90) return `${Math.round(s)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}

function StudyRowView({ r }: { r: StudyRow }) {
  return (
    <tr className="border-b border-border/50 hover:bg-surface-2">
      <td className="px-3 py-2 font-mono text-xs" title={r.study_uid}>
        {r.job_id ? (
          <Link
            to={`/case-viewer/${r.study_uid}::${r.job_id}`}
            className="text-signal-text-tint hover:underline"
          >
            {uidTail(r.study_uid)}
          </Link>
        ) : (
          uidTail(r.study_uid)
        )}
        {r.resent && (
          <span
            className="ml-2 rounded-sharp bg-yellow-500/20 text-yellow-500 px-1.5 py-0.5 text-[10px]"
            title="Study was re-sent after it first completed"
          >
            re-sent
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-sm" title={r.uploaded_by ?? ""}>
        {r.company ?? "—"}
      </td>
      <td className="px-3 py-2 text-sm text-center">
        {r.series_count || "—"}
        {r.slices != null && <span className="text-fg-faint"> ({r.slices})</span>}
      </td>
      <td className="px-3 py-2 text-xs text-fg-muted">{fmtTime(r.received_by_device)}</td>
      <td className="px-3 py-2 text-sm">
        {r.canonicalize.status === "ok" ? (
          <span
            className="inline-flex items-center gap-1.5"
            title="received by device → canonicalized (incl. queue/cold-start)"
          >
            <span className={`inline-block w-2 h-2 rounded-full ${DOT.ok}`} />
            <span className="text-fg">{fmtDur(r.canonicalize.duration_s) || "canonicalized"}</span>
          </span>
        ) : (
          <Pill c={r.canonicalize} />
        )}
      </td>
      <td className="px-3 py-2 text-sm">
        <Pill c={r.inference} />
      </td>
      <td className="px-3 py-2 text-sm text-warn">
        {r.error ? `${r.error.stage}: ${r.error.message}` : ""}
      </td>
      <td className="px-3 py-2 text-sm text-center">{r.findings ?? "—"}</td>
    </tr>
  );
}

export default function IntegrationStatus() {
  const { data: integrations } = useQuery({
    queryKey: ["integrations"],
    queryFn: fetchIntegrations,
    staleTime: 5 * 60_000,
  });
  const [key, setKey] = useState("htmedica");

  const { data, isLoading, isError, error, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ["integration-status", key],
    queryFn: () => fetchIntegrationStatus(key),
    refetchInterval: 30_000, // live updates while the page is open
  });

  return (
    <div className="mx-auto w-full max-w-[1600px] p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Upload status</h1>
          {isFetching && <span className="text-fg-faint text-xs">refreshing…</span>}
        </div>
        <select
          className="rounded-sharp border border-border-strong bg-surface-1 px-2 py-1 text-sm"
          value={key}
          onChange={(e) => setKey(e.target.value)}
        >
          {(integrations ?? [{ key: "htmedica", display_name: "HT Médica" }]).map((i) => (
            <option key={i.key} value={i.key}>
              {i.display_name}
            </option>
          ))}
        </select>
      </div>

      {isError && (
        <p className="text-warn mb-4">
          Couldn&apos;t load status ({(error as Error)?.message ?? "error"}). Admin only.
        </p>
      )}

      {data && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
            <HealthCard name="Gateway" c={data.health.gateway} />
            <HealthCard name="Canonicalize" c={data.health.canonicalize} />
            <HealthCard name="Device" c={data.health.device} />
          </div>

          <div className="overflow-x-auto rounded-sharp border border-border">
            <table className="w-full min-w-[1120px] text-left">
              <thead className="sticky top-0 z-10 bg-surface-2 text-fg-muted text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2">Study</th>
                  <th className="px-3 py-2">Sender</th>
                  <th className="px-3 py-2 text-center">Series (slices)</th>
                  <th className="px-3 py-2">Received by device</th>
                  <th className="px-3 py-2">Canonicalize</th>
                  <th className="px-3 py-2">Inference</th>
                  <th className="px-3 py-2">Error</th>
                  <th className="px-3 py-2 text-center">Findings</th>
                </tr>
              </thead>
              <tbody>
                {data.studies.map((r) => (
                  <StudyRowView key={r.study_uid + (r.received_by_device ?? "")} r={r} />
                ))}
                {data.studies.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-fg-muted" colSpan={8}>
                      No studies in this integration yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-fg-faint text-xs mt-3">
            Updated {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : "—"} ·
            auto-refreshes every 30s. Gateway-side timings (box-receive, de-identify, upload
            duration) aren&apos;t instrumented — pull them from the gateway logs after the fact.
            Inference duration is shown when captured.
          </p>
        </>
      )}

      {isLoading && !data && <p className="text-fg-muted">Loading…</p>}
    </div>
  );
}
